/* Icebox PLP facets: FacetFiltersForm (staged drawer filtering, section
   re-render of grid + filters), FacetRemove, and the toolbar/drawer UI. */

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      if (
        facetForm.dataset.plpStaging === 'true' &&
        event.target.closest('[data-plp-drawer]') &&
        event.target.id !== 'SortBy'
      ) {
        window.plpFacetsUI?.previewDrawerFilters(event);
        return;
      }
      this.debouncedOnSubmit(event);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    // Monotonic token: only the latest render may write to the page, so a
    // slow older response can never overwrite a newer filter state (PLP-6).
    const renderToken = ++FacetFiltersForm.renderToken;
    // A committed render supersedes any in-flight drawer preview (PLP-4).
    FacetFiltersForm.previewToken++;
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }
    document.querySelector('.plp-facets-toolbar__count')?.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, renderToken);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  // Discards any in-flight preview responses (used when the drawer closes
  // without applying, so a stale preview cannot overwrite the restored state).
  static invalidatePreviews() {
    FacetFiltersForm.previewToken++;
  }

  static renderPagePreview(searchParams, event) {
    const sections = FacetFiltersForm.getSections();
    const url = `${window.location.pathname}?section_id=${sections[0].section}&${searchParams}`;
    const previewToken = ++FacetFiltersForm.previewToken;

    document.getElementById('PlpFacetsDrawerFooter')?.classList.add('is-loading');

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        if (previewToken !== FacetFiltersForm.previewToken) return;
        FacetFiltersForm.renderFilters(responseText, event, { previewOnly: true });
        document.getElementById('PlpFacetsDrawerFooter')?.classList.remove('is-loading');
      })
      .catch(() => {
        if (previewToken !== FacetFiltersForm.previewToken) return;
        document.getElementById('PlpFacetsDrawerFooter')?.classList.remove('is-loading');
      });
  }

  static renderSectionFromFetch(url, event, renderToken = FacetFiltersForm.renderToken) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        if (renderToken !== FacetFiltersForm.renderToken) return;
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch(() => {
        document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');
        document.querySelector('.plp-facets-toolbar__count')?.classList.remove('loading');
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    container.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    container.querySelector('.collection')?.classList.remove('loading');

    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHTML.getElementById('ProductCount');
    if (!sourceCount) return;

    const count = sourceCount.innerHTML;
    const countValue = sourceCount?.dataset.count;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container && count) {
      container.innerHTML = count;
      if (countValue) container.dataset.count = countValue;
      container.classList.remove('loading');
    }
    if (containerDesktop && count) {
      containerDesktop.innerHTML = count;
      if (countValue) containerDesktop.dataset.count = countValue;
      containerDesktop.classList.remove('loading');
    }
    document.querySelector('.plp-facets-toolbar__count')?.classList.remove('loading');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    FacetFiltersForm.dispatchPlpFacetsUpdated();
  }

  static renderFilters(html, event, options = {}) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter');
    const facetDetailsElementsFromDom = document.querySelectorAll('#FacetFiltersForm .js-filter');

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    if (countsToRender && event?.target?.closest('.js-filter')) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementToActivate = newFacetDetailsElement.querySelector('.plp-facets-drawer__summary');

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    if (options.previewOnly) {
      const sourceCount = parsedHTML.getElementById('ProductCount');
      window.plpFacetsUI?.updateDrawerFooterCount(sourceCount?.dataset.count);
      return;
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.dispatchPlpFacetsUpdated();
  }

  static renderActiveFacets(html) {
    const activeFacetsElement = html.querySelector('.active-facets-desktop');
    const target = document.querySelector('.active-facets-desktop');
    if (activeFacetsElement && target) target.innerHTML = activeFacetsElement.innerHTML;

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static parseFacetHTML(html) {
    if (typeof html === 'string') {
      return new DOMParser().parseFromString(html, 'text/html');
    }

    return html;
  }

  static renderAdditionalElements(html) {
    const parsedHTML = FacetFiltersForm.parseFacetHTML(html);

    const sourceState = parsedHTML.getElementById('PlpFacetsState');
    const targetState = document.getElementById('PlpFacetsState');
    if (sourceState && targetState) {
      [...sourceState.attributes].forEach((attribute) => {
        targetState.setAttribute(attribute.name, attribute.value);
      });
    }

    const countIds = ['ProductCount', 'ProductCountDesktop'];
    countIds.forEach((id) => {
      const source = parsedHTML.getElementById(id);
      const target = document.getElementById(id);
      if (!source || !target) return;
      if (source.dataset.count) target.dataset.count = source.dataset.count;
      if (source.innerHTML) target.innerHTML = source.innerHTML;
    });

    window.plpFacetsUI?.refreshState();
  }

  static dispatchPlpFacetsUpdated() {
    document.dispatchEvent(new CustomEvent('plp-facets:updated'));
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    this.onSubmitForm(this.createSearchParams(this.querySelector('form')), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.renderToken = 0;
FacetFiltersForm.previewToken = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (!form?.onActiveFilterClick) return;
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class PlpFacetsUI {
  constructor() {
    this.root = document.querySelector('.plp-facets');
    if (!this.root) return;

    this.drawer = this.root.querySelector('[data-plp-drawer]');
    this.form = this.root.querySelector('#FacetFiltersForm');
    this.sortSelect = this.root.querySelector('#SortBy');
    this.committedSnapshot = null;
    this.previewTimeout = null;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    // Named bound reference (not an inline arrow) so destroy() can remove it.
    this.onFacetsUpdated = this.refreshState.bind(this);

    this.bindEvents();
    this.bindDrawerEvents();
    this.refreshState();
    document.addEventListener('plp-facets:updated', this.onFacetsUpdated);
  }

  // Removes all document-level listeners owned by this instance. Called by
  // initPlpFacets before a replacement instance is created (e.g. theme editor
  // section reloads), so stale instances can't double-handle clicks/Escape.
  destroy() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('plp-facets:updated', this.onFacetsUpdated);
    clearTimeout(this.previewTimeout);
  }

  bindEvents() {
    this.root.addEventListener('click', (event) => {
      const applyDrawerBtn = event.target.closest('[data-plp-drawer-apply]');
      if (applyDrawerBtn) {
        event.preventDefault();
        this.applyDrawer();
        return;
      }

      const clearFiltersBtn = event.target.closest('[data-plp-clear-filters], [data-plp-drawer-clear-all]');
      if (clearFiltersBtn) {
        event.preventDefault();
        this.clearFiltersAndApply();
        return;
      }

      const sortTrigger = event.target.closest('[data-plp-sort-trigger]');
      if (sortTrigger) {
        event.preventDefault();
        this.toggleSortMenu(sortTrigger);
        return;
      }

      const sortOption = event.target.closest('[data-sort-value]');
      if (sortOption) {
        event.preventDefault();
        const inDrawer = Boolean(sortOption.closest('[data-plp-drawer]'));
        const stage = inDrawer && this.isStaging();
        this.setSort(sortOption.dataset.sortValue, sortOption.dataset.sortLabel, { stage });
        if (!stage) this.closeSortMenu();
      }
    });

    this.root.addEventListener('change', (event) => {
      if (!this.isStaging()) return;

      if (event.target.closest('[data-plp-drawer] .js-filter')) {
        this.previewDrawerFilters(event);
      }
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  bindDrawerEvents() {
    if (!this.drawer || this.drawer.dataset.plpDrawerBound === 'true') return;

    this.drawer.dataset.plpDrawerBound = 'true';

    // <ds-drawer> owns open/close, scrim, Escape, scroll lock, inert and focus.
    // Every dismissal (scrim, close button, Escape, another drawer opening)
    // discards the staged selection; only Apply and Clear commit.
    this.drawer.onBeforeOpen = () => {
      this.beginStaging();
    };
    this.drawer.onClose = ({ reason } = {}) => {
      this.endStaging(reason !== 'apply' && reason !== 'clear');
    };

    this.bindDrawerAccordion();
  }

  bindDrawerAccordion() {
    const body = this.drawer?.querySelector('.plp-facets-drawer__body');
    if (!body || body.dataset.plpAccordionBound === 'true') return;

    body.dataset.plpAccordionBound = 'true';

    body.addEventListener(
      'click',
      (event) => {
        const summary = event.target.closest('.plp-facets-drawer__summary');
        if (!summary) return;

        const details = summary.closest('details.plp-facets-drawer__accordion');
        if (!details) return;

        // Closing the active section — leave other sections unchanged.
        if (details.open) {
          // Native toggle will close it; reflect that on the next frame.
          requestAnimationFrame(() => this.syncAccordionAria());
          return;
        }

        body.querySelectorAll('.plp-facets-drawer__accordion[open]').forEach((openDetails) => {
          openDetails.open = false;
        });
        requestAnimationFrame(() => this.syncAccordionAria());
      },
      true
    );
  }

  // Keep each disclosure summary's aria-expanded in sync with its details[open]
  // state. The server renders summaries with aria-expanded="false"; facets.js
  // replaces the accordion markup on every filter change, so the global one-time
  // wiring in global.js never re-applies here — this is the single source.
  syncAccordionAria() {
    this.root
      ?.querySelectorAll('details.plp-facets-drawer__accordion > .plp-facets-drawer__summary')
      .forEach((summary) => {
        const details = summary.closest('details.plp-facets-drawer__accordion');
        if (details) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      });
  }

  isStaging() {
    return this.form?.dataset.plpStaging === 'true';
  }

  onDocumentClick(event) {
    if (!this.root) return;

    const sortMenu = this.root.querySelector('[data-plp-sort-menu]');
    const sortTrigger = this.root.querySelector('[data-plp-sort-trigger]');
    if (sortMenu && !sortMenu.hidden) {
      if (!event.target.closest('[data-plp-sort]')) {
        this.closeSortMenu(sortTrigger, sortMenu);
      }
    }
  }

  onKeydown(event) {
    if (event.key !== 'Escape' || window.DsDrawer?.top) return;

    const sortMenu = this.root.querySelector('[data-plp-sort-menu]');
    if (!sortMenu || sortMenu.hidden) return;

    const sortTrigger = this.root.querySelector('[data-plp-sort-trigger]');
    const focusWasInMenu = sortMenu.contains(document.activeElement);
    this.closeSortMenu(sortTrigger, sortMenu);
    if (focusWasInMenu) sortTrigger?.focus();
  }

  captureSnapshot() {
    const facetForm = this.form?.closest('facet-filters-form');
    if (!facetForm || !this.form) return null;

    return {
      searchParams: FacetFiltersForm.searchParamsPrev || facetForm.createSearchParams(this.form),
      productCount:
        this.root.querySelector('#ProductCountDesktop')?.dataset.count ||
        this.root.querySelector('#PlpFacetsState')?.dataset.productCount ||
        '0',
      sortValue: this.sortSelect?.value,
      sortLabel: this.root.querySelector('#PlpFacetsState')?.dataset.sortLabel,
      activeCount: this.getCommittedActiveCount(),
    };
  }

  restoreSnapshot(snapshot) {
    if (!snapshot || !this.form) return;

    const params = new URLSearchParams(snapshot.searchParams);

    this.form.querySelectorAll('[data-plp-drawer] input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = params.getAll(checkbox.name).includes(checkbox.value);
    });

    this.form.querySelectorAll('[data-plp-drawer] .facets__price input').forEach((input) => {
      input.value = params.get(input.name) || '';
    });

    this.form.querySelectorAll('[data-plp-drawer] plp-price-range').forEach((element) => {
      element.syncFromInputs?.();
    });

    if (this.sortSelect && snapshot.sortValue) {
      this.sortSelect.value = snapshot.sortValue;
      this.syncSortUI(snapshot.sortValue, snapshot.sortLabel, { includeToolbar: true, includeDrawer: true });
    }

    this.updateDrawerFooterCount(snapshot.productCount);
    this.updateDrawerFilterIndicators();
  }

  clearFiltersAndApply() {
    if (!this.form) return;

    delete this.form.dataset.plpStaging;
    this.committedSnapshot = null;
    clearTimeout(this.previewTimeout);

    this.clearPendingFilters();

    const facetForm = this.form.closest('facet-filters-form');
    const searchParams = facetForm.createSearchParams(this.form);

    FacetFiltersForm.renderPage(searchParams, null);

    if (this.drawer?.isOpen) this.drawer.close({ reason: 'clear' });
  }

  clearPendingFilters() {
    if (!this.form) return;

    this.form.querySelectorAll('[data-plp-drawer] input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = false;
    });

    this.form.querySelectorAll('[data-plp-drawer] .facets__price input').forEach((input) => {
      input.value = '';
    });

    this.form.querySelectorAll('[data-plp-drawer] plp-price-range').forEach((element) => {
      element.syncFromInputs?.();
    });
  }

  openDrawer(trigger = this.root.querySelector('[data-plp-filters-open]')) {
    this.drawer?.open?.({ trigger });
  }

  closeDrawer(discardChanges = true) {
    if (this.drawer?.isOpen) {
      this.drawer.close({ reason: discardChanges ? 'dismiss' : 'apply' });
    } else {
      this.endStaging(discardChanges);
    }
  }

  beginStaging() {
    if (!this.form) return;
    this.committedSnapshot = this.captureSnapshot();
    this.form.dataset.plpStaging = 'true';
    this.updateDrawerFilterIndicators();
  }

  endStaging(discardChanges = true) {
    if (discardChanges && this.committedSnapshot) {
      this.restoreSnapshot(this.committedSnapshot);
      // Refresh facet counts back to the committed state; invalidate any
      // in-flight staged preview first so it can't win the race afterwards.
      FacetFiltersForm.invalidatePreviews?.();
      FacetFiltersForm.renderPagePreview(this.committedSnapshot.searchParams, null);
    } else {
      FacetFiltersForm.invalidatePreviews?.();
    }

    delete this.form?.dataset.plpStaging;
    this.committedSnapshot = null;
    clearTimeout(this.previewTimeout);
    this.updateDrawerFilterIndicators();
  }

  applyDrawer() {
    if (!this.form) return;

    const facetForm = this.form.closest('facet-filters-form');
    const searchParams = facetForm.createSearchParams(this.form);

    delete this.form.dataset.plpStaging;
    this.committedSnapshot = null;
    clearTimeout(this.previewTimeout);

    FacetFiltersForm.renderPage(searchParams, null);
    this.closeDrawer(false);
  }

  previewDrawerFilters(event) {
    if (!this.isStaging() || !this.form) return;

    clearTimeout(this.previewTimeout);
    this.previewTimeout = setTimeout(() => {
      const facetForm = this.form.closest('facet-filters-form');
      const searchParams = facetForm.createSearchParams(this.form);
      FacetFiltersForm.renderPagePreview(searchParams, event);
    }, 300);
  }

  toggleSortMenu(trigger = this.root.querySelector('[data-plp-sort-trigger]')) {
    const menu = this.root.querySelector('[data-plp-sort-menu]');
    if (!trigger || !menu) return;
    const isOpen = !menu.hidden;
    if (isOpen) {
      this.closeSortMenu(trigger, menu);
    } else {
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
    }
  }

  closeSortMenu(trigger = this.root.querySelector('[data-plp-sort-trigger]'), menu = this.root.querySelector('[data-plp-sort-menu]')) {
    if (!menu || !trigger) return;
    menu.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
  }

  setSort(value, label, { stage = false } = {}) {
    if (!this.sortSelect || !value) return;

    this.sortSelect.value = value;

    if (stage) {
      this.syncSortUI(value, label, { includeToolbar: false, includeDrawer: true });
      this.previewDrawerFilters();
      return;
    }

    this.syncSortUI(value, label, { includeToolbar: true, includeDrawer: true });
    this.sortSelect.dispatchEvent(new Event('input', { bubbles: true }));
  }

  syncSortUI(value, label, { includeToolbar = true, includeDrawer = true } = {}) {
    if (includeToolbar) {
      this.root.querySelectorAll('.plp-facets-sort__option[data-sort-value]').forEach((button) => {
        const isActive = button.dataset.sortValue === value;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    if (includeDrawer && this.drawer) {
      this.drawer.querySelectorAll('.plp-facets-drawer__sort-option[data-sort-value]').forEach((button) => {
        const isActive = button.dataset.sortValue === value;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      const drawerLabel = this.drawer.querySelector('[data-plp-drawer-sort-label]');
      if (drawerLabel && label) drawerLabel.textContent = label;
    }

    const state = this.root.querySelector('#PlpFacetsState');
    if (state) {
      state.dataset.sortValue = value;
      if (label) state.dataset.sortLabel = label;
    }
  }

  updateDrawerFooterCount(count) {
    if (count == null) return;
    const drawerCount = this.root.querySelector('[data-plp-drawer-product-count]');
    if (drawerCount) drawerCount.textContent = count;
    const drawerWord = this.root.querySelector('[data-plp-drawer-results-word]');
    if (drawerWord) drawerWord.textContent = Number(count) === 1 ? 'result' : 'results';
  }

  getCommittedActiveCount() {
    if (this.isStaging() && this.committedSnapshot) {
      return this.committedSnapshot.activeCount;
    }

    return Number(this.root.querySelector('#PlpFacetsState')?.dataset.activeCount || 0);
  }

  updateToolbarActiveBadge() {
    const badge = this.root.querySelector('[data-plp-active-badge]');
    if (!badge) return;

    const count = this.getCommittedActiveCount();

    badge.textContent = count;
    badge.classList.toggle('hidden', count === 0);
    badge.setAttribute('aria-label', `${count} active filters`);
  }

  updateDrawerActiveBadge() {
    const badge = this.drawer?.querySelector('[data-plp-drawer-active-badge]');
    if (!badge) return;

    const count = this.getCommittedActiveCount();

    badge.textContent = count;
    badge.classList.toggle('hidden', count === 0);
    badge.setAttribute('aria-label', `${count} active filters`);
  }

  updateDrawerClearAllVisibility() {
    if (!this.form) return;

    const clearAll = this.drawer?.querySelector('[data-plp-drawer-clear-all]');
    if (!clearAll) return;

    const count = this.getCommittedActiveCount();
    clearAll.classList.toggle('hidden', count === 0);
  }

  updateDrawerFilterIndicators() {
    const activeCount = this.getCommittedActiveCount();

    this.updateDrawerClearAllVisibility();
    this.updateDrawerActiveBadge();
    this.updateToolbarActiveBadge();

    this.root.querySelector('[data-plp-clear-filters]')?.classList.toggle('hidden', activeCount === 0);
  }

  formatProductCount(count) {
    const total = Number(count) || 0;
    return `(${total} Product${total === 1 ? '' : 's'})`;
  }

  refreshState() {
    if (this.isStaging()) return;

    const state = this.root.querySelector('#PlpFacetsState');
    const productCountDesktop = this.root.querySelector('#ProductCountDesktop');
    const productCountHidden = document.getElementById('ProductCount');
    const count =
      productCountDesktop?.dataset.count ||
      productCountHidden?.dataset.count ||
      state?.dataset.productCount ||
      '0';
    const sortValue = state?.dataset.sortValue || this.sortSelect?.value;
    const sortLabel = state?.dataset.sortLabel;

    const formattedCount = this.formatProductCount(count);

    if (productCountDesktop) {
      productCountDesktop.dataset.count = count;
      productCountDesktop.textContent = formattedCount;
    }

    if (productCountHidden) {
      productCountHidden.dataset.count = count;
      productCountHidden.textContent = formattedCount;
    }

    this.updateDrawerFooterCount(count);
    this.updateDrawerFilterIndicators();
    this.syncAccordionAria();

    if (sortValue) this.syncSortUI(sortValue, sortLabel, { includeToolbar: true, includeDrawer: true });
  }
}

const initPlpFacets = () => {
  if (!document.querySelector('.plp-facets')) {
    // Facets UI no longer on the page: drop any stale document listeners.
    window.plpFacetsUI?.destroy?.();
    window.plpFacetsUI = null;
    return;
  }

  if (window.plpFacetsUI?.root?.isConnected) {
    window.plpFacetsUI.refreshState();
    return;
  }

  // The old root was replaced (section reload): tear the old instance down
  // before binding a fresh one so document listeners never accumulate.
  window.plpFacetsUI?.destroy?.();
  window.plpFacetsUI = new PlpFacetsUI();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPlpFacets);
} else {
  initPlpFacets();
}

document.addEventListener('shopify:section:load', initPlpFacets);
